<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("common.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main class="flex flex-col space-y-8 px-12 py-10">
        <h2 tabindex="-1" class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight" ref="initialFocus">
          {{ t("pages.knocs.wizard.title") }}
        </h2>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <router-link
            to="/admin/knocs/new/linux"
            data-testid="create-linux-link"
            class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            @click="wizard.$reset()"
          >
            <div class="space-y-2">
              <ServerIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

              <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                {{ t("pages.knocs.wizard.options.linux.title") }}
              </h4>

              <p class="text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.linux.description.0") }}
                <br />
                {{ t("pages.knocs.wizard.options.linux.description.1") }}
              </p>
            </div>

            <p class="!mt-auto text-sm text-muted-foreground">
              {{ t("pages.knocs.wizard.options.linux.other") }}
            </p>
          </router-link>

          <router-link
            to="/admin/knocs/new/application"
            data-testid="create-application-link"
            class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            @click="wizard.$reset()"
          >
            <div class="space-y-2">
              <ShieldCheckIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

              <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                {{ t("pages.knocs.wizard.options.application.title") }}
              </h4>

              <p class="text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.application.description.0") }}
                <br />
                {{ t("pages.knocs.wizard.options.application.description.1") }}
              </p>
            </div>

            <p class="!mt-auto text-sm text-muted-foreground">
              {{ t("pages.knocs.wizard.options.application.other") }}
            </p>
          </router-link>

          <router-link
            to="/admin/knocs/new/proxy"
            data-testid="create-proxy-link"
            class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            @click="wizard.$reset()"
          >
            <div class="space-y-2">
              <GlobeAltIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

              <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                {{ t("pages.knocs.wizard.options.proxy.title") }}
              </h4>

              <p class="text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.proxy.description.0") }}
                <br />
                {{ t("pages.knocs.wizard.options.proxy.description.1") }}
              </p>
            </div>

            <p class="!mt-auto text-sm text-muted-foreground">
              {{ t("pages.knocs.wizard.options.proxy.other") }}
            </p>
          </router-link>

          <router-link
            class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            data-testid="create-windwos-link"
            to="/admin/knocs/new/windows"
            @click="wizard.$reset()"
          >
            <div class="space-y-2">
              <CubeIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

              <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                {{ t("pages.knocs.wizard.options.windows.title") }}
              </h4>

              <p class="text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.windows.description.0") }}
                <br />
                {{ t("pages.knocs.wizard.options.windows.description.1") }}
              </p>
            </div>

            <p class="!mt-auto text-sm text-muted-foreground">
              {{ t("pages.knocs.wizard.options.windows.other") }}
            </p>
          </router-link>

          <router-link
            to="/admin/knocs/new/script"
            data-testid="create-script-link"
            class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            @click="wizard.$reset()"
          >
            <div class="space-y-2">
              <CodeBracketIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

              <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                {{ t("pages.knocs.wizard.options.script.title") }}
              </h4>
            </div>

            <p class="!mt-auto text-sm text-muted-foreground">
              {{ t("pages.knocs.wizard.options.script.other") }}
            </p>
          </router-link>

          <a
            href="mailto:support@knocknoc.io?subject=Knoc: I have a custom integration request"
            class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
          >
            <div class="space-y-2">
              <ChatBubbleLeftRightIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

              <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                {{ t("pages.knocs.wizard.options.contact.title") }}
              </h4>
            </div>

            <p class="!mt-auto text-sm text-muted-foreground">
              {{ t("pages.knocs.wizard.options.contact.other") }}
            </p>
          </a>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { Bars3Icon } from "@heroicons/vue/24/solid";
import {
  ChatBubbleLeftRightIcon,
  CodeBracketIcon,
  CubeIcon,
  GlobeAltIcon,
  ServerIcon,
  ShieldCheckIcon,
} from "@heroicons/vue/24/outline";
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useWizardStore } from "../../pinia/wizard";

import SearchBar from "../../components/SearchBar.vue";
import SideBar from "../../components/SideBar.vue";
import AdminHeader from "../../components/AdminHeader.vue";

const { t } = useI18n();

const initialFocus = ref(null);

const sidebarOpen = ref(false);
const search = ref("");

const wizard = useWizardStore();

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});
</script>
